import { Box, Tooltip, Typography, Avatar, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import SyncIcon from '@mui/icons-material/Sync';
import { useState } from 'react';
import { cloudinaryFull, cloudinaryThumbnail } from '../../utils/formatCloudinary';
import { fCurrency } from '../../utils/formatNumber';
import { fDate, fDateTime, fMonthDayYear, fMonthDayYearShort, fMinDate } from '../../utils/formatTime';
import Iconify from '../iconify';
import Image from '../image';
import Lightbox from '../lightbox';
import { renderColorBox, stringToPrimaryColor } from '../../utils/cssStyles';
import { InvoiceApprovalStatus } from '../../utils/globalEnums';

const renderIcon = (value, icon) => (
  <Box>
    <Iconify icon={icon} width={13} height={13} /> {value}
  </Box>
);

const overflowEllipsesStyle = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const invoiceId = {
  field: 'invoiceId',
  headerName: 'PO #',
  width: 90,
  type: 'customSelect',
  getFilterValues: (row) => {
    const val = row.invoiceId;
    return [val === null || val === undefined || val === '' ? 'N/A' : val];
  },
  valueGetter: (params) => {
    const val = params.row.invoiceId;
    return val === null || val === undefined || val === '' ? 'N/A' : val;
  },
  renderCell: (params) => {
    const { value } = params;
    const displayValue = value === null || value === undefined || value === '' ? 'N/A' : value;

    return (
      <Box
        component="div"
        sx={{
          fontSize: '0.75rem',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'pre-wrap',
        }}
      >
        {displayValue}
      </Box>
    );
  },
};
const category = {
  field: 'category',
  headerName: 'Category',
  width: 90,
  type: 'customSelect',
};
const qty = { field: 'quantity', headerName: 'Qty', minWidth: 50, flex: 0.5 };
const date = {
  field: 'date',
  headerName: 'Invoice Date',
  width: 115,
  flex: 0.7,
  renderCell: (params) => (
    <Box sx={overflowEllipsesStyle}>
      {fMinDate(params.value)
        ? renderIcon(fMonthDayYearShort(params.value), 'material-symbols:calendar-today-outline')
        : ''}
    </Box>
  ),
  valueGetter: (params) => new Date(params.value),
  type: 'customDate',
};
const invoiceDate = {
  field: 'invoiceDate',
  headerName: 'Invoice Date',
  width: 115,
  flex: 0.7,
  renderCell: (params) => (
    <Box sx={overflowEllipsesStyle}>
      {params.value ? renderIcon(fMonthDayYear(params.value), 'material-symbols:calendar-today-outline') : ''}
    </Box>
  ),
  type: 'customDate',
  valueGetter: (params) => new Date(params.value),
};

const dateAdded = {
  field: 'dateAdded',
  headerName: 'Sent on',
  width: 115,
  type: 'customDate',
  flex: 0.7,
  renderCell: (params) => (
    <Box sx={overflowEllipsesStyle}>
      {renderIcon(fMonthDayYearShort(params.value), 'material-symbols:calendar-today-outline')}
    </Box>
  ),
  valueGetter: (params) => new Date(params.value),
};
const dateAddedFile = {
  field: 'dateAdded',
  headerName: 'Date Added',
  type: 'customDate',
  flex: 1,
  renderCell: (params) => renderIcon(fMonthDayYear(params.value), 'material-symbols:calendar-today-outline'),
  valueGetter: (params) => new Date(params.value),
};
const dateSaved = {
  field: 'dateSaved',
  headerName: 'Date Added',
  type: 'customDate',
  flex: 1,
  renderCell: (params) => renderIcon(fDate(params.value), 'material-symbols:calendar-today-outline'),
  valueGetter: (params) => new Date(params.value),
};

const total = {
  field: 'total',
  headerName: 'Total',
  minWidth: 70,
  flex: 0.5,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.row.total,
};
const invoiceItemTotal = {
  field: 'total',
  headerName: 'Total',
  flex: 0.5,
  type: 'customMoney',
  valueGetter: (params) => fCurrency(params.row.quantity * params.row.price + params.row.taxAmount),
};
const shipping = {
  field: 'shipping',
  headerName: 'Shipping',
  minWidth: 60,
  flex: 0.5,
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.row.shipping,
  type: 'customMoney',
};
const account = {
  field: 'account',
  headerName: 'Account',
  flex: 0.5,
};

const exists = {
  field: 'exists',
  headerName: 'Exists',
  flex: 0.5,
  align: 'center',
  renderCell: (params) => {
    const { value } = params;
    return value ? <Iconify icon="material-symbols:check-circle-outline" color="primary" /> : <></>;
  },
};
const success = {
  field: 'success',
  headerName: 'Success',
  flex: 0.5,
  align: 'right',
  headerAlign: 'right',
  renderCell: (params) => {
    const { value } = params;
    return value ? (
      <Iconify icon="material-symbols:check-circle-outline" color="primary" />
    ) : (
      <Iconify icon="ic:sharp-close" color="error" />
    );
  },
};

export default function RenderImage({ params }) {
  // if (params.row.productDetails[0].baseUOMAmount || params.row.productDetails[0].baseUOMType) {
  // }
  RenderImage.propTypes = {
    params: PropTypes.object,
  };
  const { pictures } = params.row;
  const picture = pictures[0];
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = () => {
    setSelectedImage(0);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };
  return (
    <>
      <Image
        alt="attachment"
        src={cloudinaryThumbnail(picture)}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={() => handleOpenLightbox()}
      />
      <Lightbox
        index={0}
        slides={pictures?.map((picture) => ({ src: cloudinaryFull(picture) }))}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}
const getFacilityValues = (row) => {
  if (row.multiFacility) {
    const facilityNames = Array.isArray(row.invoiceFacilities) ? row.invoiceFacilities.map((f) => f.facilityName) : [];
    return ['Multi Facility Invoice', ...facilityNames];
  }
  return row.facility ? [row.facility] : [];
};
const facility = {
  field: 'facility',
  headerName: 'Facility',
  minWidth: 100,
  flex: 1,
  renderCell: (params) => {
    const { row } = params;

    const facilityNames = Array.isArray(row.invoiceFacilities) ? row.invoiceFacilities.map((f) => f.facilityName) : [];

    const tooltipContent = row.multiFacility ? (
      facilityNames.length ? (
        <div>
          {facilityNames.map((name, idx) => (
            <div key={idx}>{name}</div>
          ))}
        </div>
      ) : (
        'No facilities'
      )
    ) : (
      ''
    );

    return (
      <Tooltip title={tooltipContent} arrow disableInteractive>
        <Box
          component="div"
          sx={{
            fontSize: '0.75rem',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'pre-wrap',
          }}
        >
          {row.multiFacility ? 'Multi Facility Invoice' : row.facility}
        </Box>
      </Tooltip>
    );
  },
  type: 'customSelect',
  valueGetter: (params) => getFacilityValues(params.row),
  getFilterValues: (row) => getFacilityValues(row),
  sortComparator: (v1, v2) =>
    (Array.isArray(v1) ? v1.join('|') : v1 || '').localeCompare(Array.isArray(v2) ? v2.join('|') : v2 || ''),
};
const group = {
  field: 'group',
  headerName: 'Group',
  flex: 1,
  renderCell: (params) => (
    <Box component="div" sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
      {params.row.group}
    </Box>
  ),
  type: 'customSelect',
  valueGetter: (params) => params.row.group,
};
const vendorFacility = {
  field: 'vendorFacility',
  headerName: 'Vendor Facility',
  flex: 1,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
  valueGetter: (params) => params.row.vendorFacility,
};
const fileName = {
  field: 'fileName',
  headerName: 'File Name',
  width: 300,
  valueGetter: (params) => params.row.fileName,
};
const sentTo = {
  field: 'sentTo',
  headerName: 'Sent To',
  minWidth: 90,
  flex: 1,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.75rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value || params.row.facility}
    </Box>
  ),
  type: 'customSelect',
  valueGetter: (params) => params.row.sentTo,
};

const status = {
  field: 'statusText',
  headerName: 'Status',
  minWidth: 120,
  flex: 0.8,
  renderCell: (params) => {
    const { row } = params;
    const text = row.fullStatusText ?? '';
    const tooltipText = row.tooltipText ?? text;
    const color = text.startsWith('Processed')
      ? 'info.main'
      : text.startsWith('Pending') || text.startsWith('Ready to Submit')
        ? 'secondary'
        : text.startsWith('Rejected by')
          ? 'warning.main'
          : 'red';
    return (
      <Tooltip
        title={text.startsWith('Booking failed') ? text.substring(text.indexOf('-') + 1).trim() : tooltipText}
        arrow
        disableInteractive
      >
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            cursor: 'pointer',
          }}
        >
          {text !== '' ? (
            renderColorBox(text.startsWith('Booking failed') ? 'Booking failed' : text, color, true)
          ) : (
            <Box />
          )}
        </Box>
      </Tooltip>
    );
  },
  type: 'customSelect',
  valueGetter: (params) => params.row.statusText,
};

// const address = {
//   field: 'address',
//   headerName: 'Address',
//   flex: 0.8,
//   type: 'customText',
// };

const address = {
  field: 'address',
  headerName: 'Address',
  minWidth: 100,
  flex: 0.8,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.75rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
  valueGetter: (params) => params.row.address,
};
const description = {
  field: 'description',
  headerName: 'Description',
  flex: 0.8,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customText',
  valueGetter: (params) => params.row.description,
};

const vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  minWidth: 100,
  flex: 0.5,
  renderCell: (params) => {
    const vendor = params.value || '';
    const { deletedVendor } = params.row;
    return (
      <Tooltip title={deletedVendor ? vendor : ''} arrow>
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            color: deletedVendor ? 'error.main' : 'inherit',
          }}
        >
          <Box>{deletedVendor ? 'Deleted vendor' : vendor}</Box>
        </Box>
      </Tooltip>
    );
  },
  type: 'customSelect',
  valueGetter: (params) => params.row.vendor,
};
const vendorInvoiceId = {
  field: 'vendorInvoiceId',
  headerName: 'Invoice #',
  minWidth: 90,
  flex: 0.8,
  type: 'customSelect',
};
const invoiceVendorId = {
  field: 'invoiceVendorId',
  headerName: 'File',
  flex: 1,
  type: 'customText',
};
const enteredBy = {
  field: 'enteredBy',
  headerName: 'Entered By',
  width: 300,
  type: 'customText',
};

const vendorItemId = {
  field: 'vendorItemId',
  headerName: 'SKU',
  flex: 0.5,
  type: 'customText',
};

const file = {
  field: 'file',
  headerName: 'File Name',
  width: 300,
  type: 'customText',
};

const uomType = {
  field: 'uomType',
  headerName: 'UOM',
  flex: 0.5,
  type: 'customText',
};

const ordered = {
  field: 'expectedAmount',
  headerName: 'Ordered',
  flex: 0.5,
  type: 'customText',
};

//Gl Items columns
const glItemId = { field: 'glItemId', headerName: 'GlItemId' };
const deleteFlag = {
  field: 'deleteFlag',
  headerName: 'deleteFlag',
};

const addedBy = {
  field: 'addedBy',
  headerName: 'Added by',
  type: 'customSelect',
  renderCell: (params) => {
    const hasColon = params.row.addedBy?.indexOf(':') > -1;
    return (
      <Tooltip title={params.row.addedBy}>
        <Typography
          variant="subtitle2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: hasColon ? 'underline' : '',
          }}
        >
          {' '}
          {params.row.addedBy?.split(':', 1)}
        </Typography>
      </Tooltip>
    );
  },
  valueGetter: (params) => params.row.addedBy,
  width: 150,
  flex: 1,
};
const approvedBy = {
  field: 'approvedBy',
  headerName: 'Submitter',
  type: 'customSelect',
  minWidth: 100,
  flex: 0.5,
  valueGetter: (params) => params.row.invoiceBookedTo?.addedBy || params.row.markedAsSent?.addedBy
};
const approver = {
  field: 'approvers',
  headerName: 'Approver',
  type: 'customSelect',
  minWidth: 120,
  flex: 1.5,
  valueGetter: (params) => params.row.approvers?.map((r) => r.approverFullName).join(', '),
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.approvers?.map((approver) => approver.approverFullName),
  renderCell: (params) => (
    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
      {params.row.approvers?.map((a) => {
        const isPending = a.status === InvoiceApprovalStatus.Pending;
        const avatarColor = stringToPrimaryColor(a.approverFullName);

        return (
          <Tooltip
            key={a.id}
            title={
              isPending ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                    Pending approval from{' '}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                    {a.approverFullName}
                  </Typography>
                </Box>
              ) : a.status === InvoiceApprovalStatus.Approved ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                    Approved by {a.approverFullName}
                  </Typography>
                  {a.approvalDate && (
                    <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                      {fDateTime(a.approvalDate)}
                    </Typography>
                  )}
                </Box>
              ) : (
                a.approverFullName
              )
            }
          >
            <Grid item>
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: isPending ? '#ffffff' : avatarColor,
                  color: isPending ? avatarColor : '#ffffff',
                  border: isPending ? '1px solid black' : 'none',
                  borderColor: isPending ? avatarColor : 'none',
                  width: 30,
                  height: 30,
                  fontSize: 14,
                  fontWeight: 'regular',
                }}
              >
                {a.approverFullName
                  .toUpperCase()
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </Avatar>
            </Grid>
          </Tooltip>
        );
      })}
    </Grid>
  ),
};
const glCode = {
  field: 'glCode',
  headerName: 'GL Code',
  type: 'customSelect',
  minWidth: 100,
  flex: 1,
};
const code = {
  field: 'code',
  headerName: 'GL Code',
  type: 'customText',
  renderCell: (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box>{params.row.code}</Box>
      {!params.row.isNew && (
        <Tooltip title="update existing GL info">
          <SyncIcon sx={{ color: 'info.main' }} />
        </Tooltip>
      )}
    </Box>
  ),
  minWidth: 100,
  flex: 1,
};

const desc = {
  field: 'subcategory',
  headerName: 'GL Name',
  type: 'customSelect',
  minWidth: 100,
  flex: 1,
};
const descr = {
  field: 'description',
  headerName: 'GL Name',
  type: 'customText',
  minWidth: 100,
  flex: 1,
};

const deptGLName = {
  field: 'categoryDesc',
  headerName: 'Department GL Name',
  type: 'customSelect',
  minWidth: 100,
  flex: 1,
};
const deptGLNameAPI = {
  field: 'categoryDesc',
  headerName: 'Department GL Name',
  renderCell: (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box>{params.row.categoryDesc}</Box>
      {!params.row.isNew && !params.row.code && (
        <Tooltip title="update existing GL info">
          <SyncIcon sx={{ color: 'info.main' }} />
        </Tooltip>
      )}
    </Box>
  ),
  type: 'customText',
  minWidth: 100,
  flex: 1,
};
const deptGLCode = {
  field: 'category',
  headerName: 'Department GL Code',
  type: 'customSelect',
  renderCell: (params) => `${params.row.category ?? params.row.categoryKey ?? ''}`,
  valueGetter: (params) => `${params.row.category ?? params.row.categoryKey ?? ''}`,
  minWidth: 100,
  flex: 1,
};

const GL_SETTINGS = [facility, group, deptGLCode, deptGLName, glCode, desc];
const GL_API_SETTINGS = [deptGLCode, deptGLNameAPI, code, descr];

const NEW_INVOICES = [
  invoiceId,
  group,
  facility,
  address,
  status,
  shipping,
  qty,
  total,
  vendor,
  date,
  dateAdded,
  addedBy,
];
const PENDING_INVOICES = [
  invoiceId,
  category,
  group,
  facility,
  sentTo,
  address,
  status,
  shipping,
  qty,
  total,
  vendor,
  date,
  dateAdded,
  addedBy,
  approver,
];
const COMPLETED_INVOICES = [
  invoiceId,
  group,
  facility,
  shipping,
  total,
  vendor,
  vendorInvoiceId,
  date,
  dateAdded,
  addedBy,
  approver,
  approvedBy,
];

const INVOICE = [invoiceId, facility, group, sentTo, address, status, shipping, qty, total, vendor, vendorInvoiceId];
const SINGLE_INVOICE = [vendorItemId, description, ordered, uomType, invoiceItemTotal];
const INVOICE_GL_ITEM = [glItemId, deleteFlag];

const STATEMENTS = [total, file, dateSaved];
const STATEMENT_LINES = [vendorInvoiceId, facility, vendorFacility, account, exists];
const INVOICE_FILES = [fileName, enteredBy, dateAddedFile];
const UPLOAD_PDF = [invoiceVendorId, success];
const UPLOAD_STATEMENTS = [vendorInvoiceId, facility, vendorFacility, account, invoiceDate, total, exists];

export {
  SINGLE_INVOICE,
  INVOICE,
  NEW_INVOICES,
  PENDING_INVOICES,
  COMPLETED_INVOICES,
  INVOICE_FILES,
  STATEMENTS,
  STATEMENT_LINES,
  UPLOAD_PDF,
  UPLOAD_STATEMENTS,
  INVOICE_GL_ITEM,
  GL_SETTINGS,
  GL_API_SETTINGS,
};
